"use client";

import { Button } from "@package/subframe/components/Button";
import { TextField } from "@package/subframe/components/TextField";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import {
  getFindOrganisationQueryControllerFindBySlugQueryKey,
  useFindOrganisationQueryControllerFindBySlug,
  useUpdateOrganisationCommandControllerUpdate,
} from "@/generated/organisations/organisations";

export const Route = createFileRoute(
  "/_authenticated/organisations/$slug/configure",
)({
  component: OrganisationConfigurePage,
});

/** Default hot holding floor (°C) when the org hasn't set a policy (NAU-220). */
const DEFAULT_HOT_HOLDING_MIN_TEMP_C = 65;

function OrganisationConfigurePage() {
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();
  const { isPlatformAdmin, memberships } = useAuth();
  const { refetch: refetchOrganisations } = useOrganisation();
  const { data: orgData, isLoading } =
    useFindOrganisationQueryControllerFindBySlug(slug);
  const organisation = orgData?.data;

  // Defence-in-depth: the Configure tab is hidden for non-admins, but a typed
  // URL would still render this page. The backend enforces the real gate.
  const canConfigure =
    isPlatformAdmin ||
    memberships.some((m) => m.role === "ADMIN" && m.status === "ACTIVE");

  const updateMutation = useUpdateOrganisationCommandControllerUpdate();

  const [thresholdRaw, setThresholdRaw] = useState("");

  // Seed the input from the saved value once the org loads.
  useEffect(() => {
    if (organisation?.hotHoldingMinTempC != null) {
      setThresholdRaw(String(organisation.hotHoldingMinTempC));
    }
  }, [organisation?.hotHoldingMinTempC]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (!organisation) {
    return (
      <span className="text-body font-body text-error-700">
        Organisation not found.
      </span>
    );
  }

  if (!canConfigure) {
    return (
      <span className="text-body font-body text-subtext-color">
        You don&apos;t have permission to configure this organisation.
      </span>
    );
  }

  const parsed = thresholdRaw.trim() === "" ? null : Number(thresholdRaw);
  const isInvalid =
    parsed !== null && (!Number.isFinite(parsed) || parsed < 0 || parsed > 100);

  const handleSave = async () => {
    if (isInvalid) {
      toast.error("Enter a temperature between 0 and 100°C.");
      return;
    }
    try {
      // Re-send the full payload — name/postcode/country are required by the
      // update DTO (there is no partial update for organisations).
      await updateMutation.mutateAsync({
        slug,
        data: {
          name: organisation.name,
          postcode: organisation.postcode,
          country: organisation.country,
          notes: organisation.notes,
          hotHoldingMinTempC: parsed ?? undefined,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: getFindOrganisationQueryControllerFindBySlugQueryKey(slug),
      });
      // Refresh the cached organisations list too — it powers the offline hot
      // holding floor used by the practitioner reading form (NAU-220).
      await refetchOrganisations();
      toast.success("Hot holding policy saved.");
    } catch {
      toast.error("Failed to save hot holding policy.");
    }
  };

  return (
    <div className="flex w-full max-w-md flex-col gap-6">
      <div className="flex flex-col gap-1">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Hot holding policy
        </span>
        <span className="text-body font-body text-subtext-color">
          The minimum hot holding temperature for food across this nursery
          group. A delivery or service reading below this floor flags an
          out-of-range alert and requires a practitioner note. Defaults to{" "}
          {DEFAULT_HOT_HOLDING_MIN_TEMP_C}°C when unset.
        </span>
      </div>

      <TextField
        label="Hot holding temperature floor (°C)"
        helpText="UK legal minimum is 63°C; many providers use 65°C as guidance. Enter a value to set the policy."
        error={isInvalid}
      >
        <TextField.Input
          type="number"
          step="0.1"
          inputMode="decimal"
          placeholder={String(DEFAULT_HOT_HOLDING_MIN_TEMP_C)}
          value={thresholdRaw}
          onChange={(e) => setThresholdRaw(e.target.value)}
        />
      </TextField>

      <Button
        variant="brand-primary"
        size="medium"
        loading={updateMutation.isPending}
        disabled={updateMutation.isPending || isInvalid || parsed === null}
        onClick={handleSave}
      >
        Save policy
      </Button>
    </div>
  );
}
